import {inject, injectable} from "inversify";
import {makeAutoObservable, observable, runInAction} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {IRound} from "data/types/rounds";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {RoundStatus} from "data/enums";
import {last, first} from "lodash";

export interface IRoundsStore {
	get rounds(): IRound[];

	get activeRound(): IRound | undefined;

	get pastRounds(): IRound[];

	fetchRounds(): Promise<void>;
}

@injectable()
export class RoundsStore implements IRoundsStore {
	@observable private _rounds: IRound[] = [];

	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	get rounds(): IRound[] {
		return this._rounds;
	}

	get activeRound(): IRound | undefined {
		return this.liveRound || this.lastComplete || this.firstOpen;
	}

	get liveRound() {
		return this._rounds.find((round) => round.status === RoundStatus.Live);
	}

	get lastComplete() {
		return last(this._rounds.filter((round) => round.status === RoundStatus.Complete));
	}

	get firstOpen() {
		return first(this._rounds.filter((round) => round.status === RoundStatus.Open));
	}

	get pastRounds() {
		return this._rounds.filter((it) =>
			[RoundStatus.Live, RoundStatus.Complete].includes(it.status)
		);
	}

	public async fetchRounds(): Promise<void> {
		const {data} = await this._jsonProvider.rounds();
		runInAction(() => {
			this._rounds = data.items;
		});
	}
}
